import { Link } from 'react-router-dom';
import { Heart, Sparkles, BarChart3, ChevronRight } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Badge } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import { formatCurrency } from '@/lib/utils';
import type { GapEntry, Item } from '@/lib/types';

const MENU = [
  {
    icon: Heart,
    title: 'View Wishlist',
    description: 'Items you want to purchase',
    href: '/wardrobe/items?status=wishlist',
    badge: undefined as string | undefined,
  },
  {
    icon: Sparkles,
    title: 'AI Suggestions',
    description: 'Personalized recommendations based on your wardrobe',
    href: '/before-buy/ai-suggestions',
    badge: 'AI',
  },
  {
    icon: BarChart3,
    title: 'Wardrobe Gap Report',
    description: 'Analyze your wardrobe against an ideal collection',
    href: '/before-buy/gap-report',
    badge: undefined,
  },
];

export function BeforeBuyHubPage() {
  const { data: gapData } = useApi<{ gaps: GapEntry[] }>('/gap-report');
  const { data: wishlistData } = useApi<{ items: Item[] }>('/items?status=wishlist');

  const gapCount = (gapData?.gaps ?? []).filter((g) => g.status === 'missing' || g.status === 'under').length;
  const wishlist = wishlistData?.items ?? [];
  const wishlistCount = wishlist.length;
  const wishlistValue = wishlist.reduce((acc, i) => acc + (i.cost ?? 0), 0);

  return (
    <Page>
      <PageHeader section="Before You Buy" />
      <Main className="space-y-6">
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-6 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Smart Shopping</h1>
          <p className="text-sm text-muted-foreground">
            Make informed decisions about your next purchase with AI-powered insights.
          </p>
        </div>

        <div className="space-y-3">
          {MENU.map((option) => (
            <Link key={option.title} to={option.href}>
              <Card className="p-4 flex items-center gap-4 hover:border-primary/30 transition-colors group">
                <div className="p-3 bg-secondary rounded-xl group-hover:bg-accent/20 transition-colors">
                  <option.icon className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-foreground">{option.title}</h3>
                    {option.badge && <Badge tone="accent">{option.badge}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </Card>
            </Link>
          ))}
        </div>

        <Card className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Quick Overview</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{wishlistCount}</p>
              <p className="text-xs text-muted-foreground">Wishlist Items</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{gapCount}</p>
              <p className="text-xs text-muted-foreground">Wardrobe Gaps</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-accent-foreground">{formatCurrency(wishlistValue)}</p>
              <p className="text-xs text-muted-foreground">Wishlist Value</p>
            </div>
          </div>
        </Card>
      </Main>
    </Page>
  );
}
